// utils.js



import { db } from '../../../components/firebase';
import { doc, getDoc } from 'firebase/firestore';
import axios from 'axios';

/* ===================== Base ===================== */
const CF_BASE =
  (process.env.REACT_APP_CF_BASE && process.env.REACT_APP_CF_BASE.replace(/\/+$/, ''))
  || 'https://europe-west3-starwa-94ac5.cloudfunctions.net';

/* ===================== Helpers ===================== */
const newMap = () => Object.create(null);
const toNum = (x) => {
  const v = Number(x);
  return Number.isFinite(v) ? v : null;
};
const round = (x, d = 2) => {
  const v = toNum(x);
  if (v === null) return null;
  const k = 10 ** d;
  return Math.round(v * k) / k;
};

// ——— убираем undefined / NaN / ±Infinity из вложенных объектов перед отправкой на CF
const sanitize = (data) =>
  JSON.parse(JSON.stringify(data, (_, v) => {
    if (v === undefined || Number.isNaN(v) || v === Infinity || v === -Infinity) return null;
    return v;
  }));

/* ===================== Caches ===================== */
let cachedData = newMap();
let lastFetchTime = newMap();
let fetchPromise = newMap();
const CACHE_DURATION = 10 * 60 * 1000; // 10 мин



let cachedHistoricalData = newMap();
let lastHistoricalFetchTime = newMap();
const HISTORICAL_CACHE_DURATION = 10 * 60 * 1000;

// id -> SYMBOL (в UPPERCASE)
let idToSymbolMap = newMap();

// Selected tokens cache (для /getCoinGeckoSelected)
let cachedSelected = [];
let lastSelectedFetch = 0;
const SELECTED_TTL = 5 * 60 * 1000; // 5 минут





// utils.js
export async function fetchSelectedTokensWithCache({ force = false } = {}) {
  const now = Date.now();
  if (!force && cachedSelected.length && (now - lastSelectedFetch < SELECTED_TTL)) {
    return cachedSelected.slice();
  }

  // кандидаты URL (иногда live=1 может давать задержки)
  const urls = [
    `${CF_BASE}/getCoinGeckoSelected?live=1&_ts=${now}`,
    `${CF_BASE}/getCoinGeckoSelected?_ts=${now}`,
  ];

  const tryOnce = async (url, timeout) => {
    const { data } = await axios.get(url, {
      timeout,
      headers: { 'Cache-Control': 'no-cache', Pragma: 'no-cache' },
    });
    const items = Array.isArray(data) ? data : Object.values(data || {});
    // обновим сопоставление id → SYMBOL
    try {
      items.forEach((t) => {
        if (t?.id && t?.symbol) idToSymbolMap[t.id] = String(t.symbol).toUpperCase();
      });
    } catch {}
    return items;
  };

  let lastErr = null;
  // 4 попытки с эксп. бэкофом и альтернативным URL
  const timeouts = [6000, 9000, 12000, 14000];
  for (let i = 0; i < timeouts.length; i++) {
    for (const u of urls) {
      try {
        const items = await tryOnce(u, timeouts[i]);
        if (items?.length) {
          cachedSelected = items;
          lastSelectedFetch = now;
          return items.slice();
        }
      } catch (e) {
        lastErr = e;
      }
    }
    // небольшой джиттер между раундами
    await new Promise((r) => setTimeout(r, 150 + i * 150));
  }

  // Fallback 1: Firestore кэш, если есть
  try {
    // пробуем как вложенный документ 'cache/coingecko/meme_coins/latest' аналогично твоему коду
    const snap1 = await getDoc(doc(db, 'cache', 'coingecko', 'selected', 'latest'));
    if (snap1.exists() && Array.isArray(snap1.data()?.data)) {
      const items = snap1.data().data;
      items.forEach((t) => {
        if (t?.id && t?.symbol) idToSymbolMap[t.id] = String(t.symbol).toUpperCase();
      });
      cachedSelected = items;
      lastSelectedFetch = Date.now();
      return items.slice();
    }
    // альтернативно: плоский документ 'cache/coingecko' со структурой { selected: { data: {...} } }
    const snap2 = await getDoc(doc(db, 'cache', 'coingecko'));
    if (snap2.exists()) {
      const maybe = snap2.data()?.selected?.data;
      const items = maybe ? Object.values(maybe) : [];
      if (items.length) {
        items.forEach((t) => {
          if (t?.id && t?.symbol) idToSymbolMap[t.id] = String(t.symbol).toUpperCase();
        });
        cachedSelected = items;
        lastSelectedFetch = Date.now();
        return items.slice();
      }
    }
  } catch {}

  // Fallback 2: топ-монеты из другого эндпойнта, чтобы UI не пустел
  try {
    const top = await fetchTokenDataWithCache(1, 50);
    if (top?.length) {
      cachedSelected = top.slice(0, 20);
      lastSelectedFetch = Date.now();
      return cachedSelected.slice();
    }
  } catch {}

  console.error('fetchSelectedTokensWithCache failed:', lastErr?.message || lastErr || 'unknown');
  return cachedSelected.slice(); // последнее успешное значение (или пусто)
}





/* ===================== Coin data ===================== */
export const fetchTokenDataWithCache = async (page = 1, perPage = 100) => {
  const cacheKey = `top_${page}_${perPage}`;
  const now = Date.now();

  if (!cachedData[cacheKey]) cachedData[cacheKey] = [];
  if (lastFetchTime[cacheKey] == null) lastFetchTime[cacheKey] = 0;

  if (cachedData[cacheKey].length > 0 && now - lastFetchTime[cacheKey] < CACHE_DURATION) {
    return cachedData[cacheKey];
  }
  if (fetchPromise[cacheKey]) {
    return await fetchPromise[cacheKey];
  }

  const fetchTokenData = async (retryCount = 0, maxRetries = 4) => {
    try {
      const response = await fetch(`${CF_BASE}/getCoinGeckoData?page=${page}&per_page=${perPage}`, { headers: { Accept: 'application/json' } });
      if (!response.ok) {
        if (response.status === 429 && retryCount < maxRetries) {
          const delay = Math.pow(2, retryCount) * 1500;
          await new Promise((r) => setTimeout(r, delay));
          return fetchTokenData(retryCount + 1, maxRetries);
        }
        throw new Error(`HTTP ${response.status}`);
      }
      const data = await response.json();
      if (!data || typeof data !== 'object' || Object.keys(data).length === 0) {
        throw new Error('Empty CoinGecko payload');
      }
      const tokenArray = Object.entries(data).map(([id, coin]) => ({ ...coin, id }));
      try {
        tokenArray.forEach(coin => { if (coin.id && coin.symbol) idToSymbolMap[coin.id] = String(coin.symbol).toUpperCase(); });
      } catch { /* noop */ }
      return tokenArray;
    } catch (error) {
      try {
        const snap = await getDoc(doc(db, 'cache', 'coingecko'));
        const fsCache = snap.exists() ? snap.data() : {};
        if (fsCache[cacheKey]?.data) {
          return Object.entries(fsCache[cacheKey].data).map(([id, coin]) => ({ ...coin, id }));
        }
        return cachedData[cacheKey] || [];
      } catch {
        return cachedData[cacheKey] || [];
      }
    }
  };

  fetchPromise[cacheKey] = fetchTokenData();
  try {
    const data = await fetchPromise[cacheKey];
    if (Array.isArray(data) && data.length > 0) {
      cachedData[cacheKey] = data;
      lastFetchTime[cacheKey] = now;
    }
    return data;
  } finally {
    fetchPromise[cacheKey] = null;
  }
};



/* ===================== Historical ===================== */
export const fetchHistoricalTokenData = async (coinId, days = 1) => {
  if (!coinId) return [];

  // базовый маппер для самых частых coingecko id → символ
  const commonIdToSym = {
    bitcoin: 'BTC',
    ethereum: 'ETH',
    tether: 'USDT',
    binancecoin: 'BNB',
    solana: 'SOL',
    ripple: 'XRP',
    cardano: 'ADA',
    dogecoin: 'DOGE',
    tron: 'TRX',
    polkadot: 'DOT',
    chainlink: 'LINK',
   
  };

  const raw = String(coinId).trim();
  const upperRaw = raw.toUpperCase();
  const symbolMap = { LSETH: 'STETH' };

  // 1) прямой тикер
  let symbol = symbolMap[upperRaw] || upperRaw;

  // 2) id → SYMBOL, если есть
  if (!/^[A-Z0-9]{2,10}$/.test(symbol)) {
    const viaMap = idToSymbolMap[raw] || idToSymbolMap[raw.toLowerCase()];
    if (viaMap) symbol = String(viaMap).toUpperCase();
  }

  // 3) частые coingecko id
  if (!/^[A-Z0-9]{2,10}$/.test(symbol)) {
    const viaCommon = commonIdToSym[raw.toLowerCase()];
    if (viaCommon) symbol = viaCommon;
  }

  const cacheKey = `${symbol}_${days}`;
  const now = Date.now();

  if (cachedHistoricalData[cacheKey] && lastHistoricalFetchTime[cacheKey] && now - lastHistoricalFetchTime[cacheKey] < HISTORICAL_CACHE_DURATION) {
    return cachedHistoricalData[cacheKey].slice();
  }

  try {
    const response = await fetch(`${CF_BASE}/getHistOHLCV?fsym=${encodeURIComponent(symbol)}&hours=${days * 24}`);
    if (!response.ok) {
      if (response.status === 404) return [];
      if (response.status === 429) return cachedHistoricalData[cacheKey] || [];
      throw new Error(`HTTP ${response.status}`);
    }
    const data = await response.json();
    if (data.Response === 'Error') return [];
    const formatted = data.Data?.Data?.filter(p => toNum(p?.close) > 0).map(p => ({ t: Number(p.time) * 1000, p: Number(p.close) })) || [];
    cachedHistoricalData[cacheKey] = formatted;
    lastHistoricalFetchTime[cacheKey] = now;
    return formatted;
  } catch {
    return cachedHistoricalData[cacheKey] || [];
  }
};


/* ===================== OHLC for KLineCharts ===================== */
export async function loadKlines(symbol, period /* {span, type} */, from, to, exchange) {
  const q = new URLSearchParams({
    symbol: String(symbol || 'BTC'),
    span: String(period?.span ?? 1),
    type: String(period?.type ?? 'hour'), // 'minute'|'hour'|'day'
    limit: '500',
  });
  if (from) q.set('from', String(from));
  if (to)   q.set('to',   String(to));
  if (exchange) q.set('exchange', String(exchange)); // 'binance' | 'bybit'

  // Если фронт идёт на Functions по домену Cloud Functions:
  const url = `${CF_BASE}/getKlines?${q.toString()}`;

  // Если у тебя настроен rewrite на тот же домен фронта,
  // можно заменить на: const url = `/getKlines?${q.toString()}`;

  try {
    const res = await fetch(url, { cache: 'no-store', headers: { Accept: 'application/json' } });
    if (!res.ok) return [];
    const data = await res.json();
    // Ожидаемый KLineCharts формат уже приходит из CF:
    // [{ timestamp, open, high, low, close, volume }, ...]
    return Array.isArray(data) ? data : [];
  } catch {
    return [];
  }
}








